import _ from 'lodash'
import nestedProperty from 'nested-property'
import moment from 'moment'
import jwt_decode from 'jwt-decode' // eslint-disable-line camelcase
import store from '@/store'

const isSubset = (object1, object2) => _.some(object2, (val, key) => _.isEqual(val, object1[key]))

export const checkScope = (required, available) => {
  let cleaned = required.filter(_.isString)
  return _.difference(cleaned, available).length === 0
}

// Get value from headers
export const fromHeaders = (headers, item) => {
  const makeData = (header) => (
    {label: header.label, value: nestedProperty.get(item, header.value)}
  )
  return _(headers).map(makeData).value()
}

// Return values for a multi-select
export const multiselectValue = (values, items, from) => {
  let val = values.length > 0
    ? _.filter(items, item => _.includes(values, item[from]))
    : null
  return val
}

export const multiselectItems = (items, from) => items ? items.map(item => item[from]) : []
const onNull = (result, value, key) => {
  if (value !== null && value !== '' && value !== 0) {
    result[key] = value
  }
}
const onArrayMerge = (result, value, key) => {
  if (_.isArray(value) && !_.isEmpty(value)) {
    result[key] = mergeIds(value)
  } else {
    result[key] = value
  }
}
const onArrayList = (result, value, key) => {
  if (_.isArray(value) && !_.isEmpty(value)) {
    result[key] = listIds(value)
  } else {
    result[key] = value
  }
}
const onObject = (result, value, key) => { if (!_.isObject(value)) { result[key] = value } }
const listField = (itemArray, field) => itemArray.map(item => item[field])

export const accumulate = stats => {
  let ret = _.reduce(stats, (result, stat) => {
    result[stat.name] = stat.value
    return result
  }, {})
  return ret
}
export const mergeField = (itemArray, field) => _.join(itemArray.map(item => item[field]), ',')
export const mergeIds = (itemArray) => mergeField(itemArray, 'id')
export const listIds = (itemArray) => listField(itemArray, 'id')
export const filterNull = (item) => _.transform(item, onNull, {})
export const compactAndFilter = item => _(item).transform(onNull, {}).transform(onArrayMerge, {}).value()
export const compressAndFilter = item => _(item).transform(onNull, {}).transform(onArrayList, {}).value()

export const newFromPayload = (old, payload) => {
  const data = {...old}
  const { key, value } = payload
  var newData = {}
  nestedProperty.set(newData, key, value)
  return {...data, ...newData}
}

export const makeAccessors = headers => {
  let usable = _.reject(headers, {sortable: false})
  return _.reduce(usable, (result, header) => {
    if (header.inTable === undefined) {
      result[header.label] = header.orig ? header.orig : header.value
    }
    return result
  }, {})
}

export const getChildren = data => {
  return _.reduce(data, (result, value, key) => {
    if (_.isArray(value)) {
      result.push({module: key, items: value})
    }
    return result
  }, [])
}

export const updateSelection = (item, selection, value, emitSelection) => {
  let res = value ? _.concat(selection, item) : _.reject(selection, {id: item.id})
  emitSelection(res)
}

export const getValue = (item, path) => nestedProperty.get(item, path)
export const setValue = (item, path, value) => nestedProperty.set(item, path, value)
export const isSelected = (item, selection) => _.some(selection, {id: item.id})

export const filterActions = (actions, context, scopes) => {
  if (actions === undefined) {
    return []
  }
  let scoped = _.filter(actions, action => action.scope === undefined || checkScope(action.scope, scopes))
  return _.filter(
    scoped, action => (action.filter === undefined || isSubset(action.filter, context))
  )
}

export const filterMenus = (menus, scopes) => {
  let scoped = _.filter(menus, menu => menu.scope === undefined || checkScope(menu.scope, scopes))
  let filtered = _.map(scoped, menu => {
    return menu.children
      ? {...menu, children: filterMenus(menu.children, scopes)}
      : menu
  })
  return filtered
}

export const cleanData = item => _(item).transform(onNull, {}).transform(onObject, {}).value()
export const makeFormData = item => {
  let form = new FormData()
  _.each(item, (value, key) => {
    form.append(key, value)
  })
  return form
}

export const setStatus = (client, module, commit, dispatch, payload, status) => {
  const { context } = payload
  commit('SET_LOADING', true)
  commit('UPDATE_REQUEST', payload)
  return new Promise((resolve, reject) => {
    client.putCustom(module, `${module}/${context.id}/status/${status}`, {}, context.params)
      .then(response => {
        commit('UPDATE_SUCCESS', response)
        commit('SET_LOADING', false)
        resolve(response)
      })
      .catch(error => {
        console.log(error)
        commit('UPDATE_ERROR', error)
        commit('SET_LOADING', false)
        reject(error)
      })
  })
}

const dataSorter = (sortBy) => {
  if (_.startsWith(sortBy, 'date')) {
    return item => moment(getValue(item, sortBy))
  }
  if (_.startsWith(sortBy, 'count')) {
    return item => parseInt(getValue(item, sortBy))
  }
  return item => getValue(item, sortBy)
}

export const sortData = (items, pagination) => {
  const { sortBy, descending, page, rowsPerPage } = pagination
  const sorter = dataSorter(sortBy)
  let sorted = _.orderBy(items, sorter, descending ? 'desc' : 'asc')
  if (rowsPerPage > 0) {
    return sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage)
  }
  return sorted
}

const singularMap = {
  regions: 'region',
  cities: 'city',
  districts: 'district',
  boardtypes: 'board_type',
  boardsides: 'board_side',
  boards: 'board',
  faces: 'face',
  clients: 'client',
  baskets: 'basket',
  visuals: 'visual',
  campaigns: 'campaign',
  bookings: 'booking',
  displays: 'display',
  users: 'user',
  available: 'face',
  printtypes: 'print_type',
  interventiontypes: 'intervention_type',
  incidenttypes: 'incident_type',
  incidents: 'incident',
  roles: 's_role',
}

export const singular = term => singularMap[term]

const pluralMap = {
  newnotifications: 'notifications',
  interventiontypes: 'intervention_types',
  incidenttypes: 'incident_types',
  boardtypes: 'board_types',
  boardsides: 'board_sides',
  printtypes: 'print_types',
  bookingsview: 'view_bookings',
  futurview: 'view_last_booked_faces',
  faceslist: 'view_faces',
  statsdisplayregions: 'view_stat_display_series',
  statsdisplayprints: 'view_stat_print_series',
  statsincidentregions: 'view_stat_incident_regions_series',
  statsincidentusers: 'view_stat_incident_users_series',
  boardsview: 'view_boards',
  available: 'faces',
  roles: 's_roles',
}

export const plural = term => pluralMap[term] ? pluralMap[term] : term

const customNames = {
  boardsview: 'boards/view',
  bookingsview: 'bookings/view',
  futurview: 'faces/lasts',
  faceslist: 'faces/view',
  statsdisplayregions: 'stats/display/region',
  statsdisplayprints: 'stats/display/print',
  statsincidentregions: 'stats/incident/region',
  statsincidentusers: 'stats/incident/user',
  available: 'faces/available',
  pendingdisplays: 'displays',
  offdisplays: 'displays',
  tenancy: 'stats/tenancy',
}

export const translate = term => customNames[term] ? customNames[term] : term

export const makeData = (data, method) => {
  if (data.photo !== undefined) {
    let cleaned = cleanData(data)
    let form = makeFormData(cleaned)
    form.append('photo', data.photo)
    if (method === 'put') {
      form.append('_method', 'PUT')
    }
    return {newData: form, method: 'post'}
  }
  return {newData: JSON.stringify(data), method}
}

export const getToken = () => localStorage.getItem('_gespantoken')
export const getProfile = (token) => {
  let found = token || localStorage.getItem('_gespantoken')
  return found ? jwt_decode(found) : {}
}

export const checkToken = token => {
  if (token) {
    let now = Date.now() / 1000
    const decoded = jwt_decode(token)
    const { exp } = decoded
    if (exp > now) {
      return true
    }
  }
  return false
}

export const getParams = context => {
  let soft = store.getters['ui/useSoftAvailability']
  let trashed = store.getters['ui/includeArchive']
  let extra = trashed
    ? { soft, trashed }
    : { soft }
  let orig = context.params ? context.params : {}
  return {...orig, ...extra}
}

export const filterItems = (items, payload) => {
  if (_.isArray(payload)) {
    let plucked = _.map(payload, 'id')
    return _.reject(items, item => _.includes(plucked, item.id))
  }
  return _.reject(items, {id: payload})
}

export const notifyUser = (notif) => {
  // Let's check whether notification permissions have already been granted
  if (Notification.permission === 'granted') {
    // If it's okay let's create a notification
    Notification(notif.title, { body: notif.text })
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission(function (permission) {
      // If the user accepts, let's create a notification
      if (permission === 'granted') {
        Notification(notif.title, { body: notif.text })
      }
    })
  }
}

export const computeFaceCount = (faces) => {
  let groupedByBoardId = _.groupBy(faces, 'board_id')
  const reducer = (result, faceArray, groupId) => {
    result[groupId] = _.groupBy(faceArray, 'board_side_id')
    return result
  }
  let groupedBySide = _.reduce(groupedByBoardId, reducer, {})
  const badger = (result, faceArray, side) => [
    ...result,
    {
      ...faceArray[0],
      badge: faceArray.length,
    },
  ]
  const flattener = (result, sideObj, groupId) => {
    let sides = _.reduce(sideObj, badger, [])
    return [...result, ...sides]
  }
  return _.reduce(groupedBySide, flattener, [])
}

const makeRegex = needle => {
  if (needle && needle !== '') {
    let textArray = needle.split(' ').map(item => item.toLowerCase())
    let mapped = textArray.map(text => `(?=.*${text})`).join('')
    let regexString = `^${mapped}.*$`
    return new RegExp(regexString)
  }
}

const extractValues = (item, extractors) => extractors.map(
  target => nestedProperty.get(item, target.orig ? target.orig : target.value)
)

const hasMatch = (regex, item, extractors) => {
  let joinedString = extractValues(item, extractors).join(' ').toLowerCase()
  return regex.test(joinedString)
}

export const smartFilter = (needle, haystack, extractors) => {
  let regex = makeRegex(needle)
  return regex
    ? haystack.filter(item => hasMatch(regex, item, extractors))
    : haystack
}
